import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export const usePhotoUpload = () => {
  const { user } = useAuthContext();
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = useCallback(async (file: File): Promise<string | null> => {
    if (!user) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return null;
    }

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image must be less than 5MB", variant: "destructive" });
      return null;
    }

    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-photos")
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      setUploading(false);
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("profile-photos")
      .getPublicUrl(fileName);

    // Save to profile_photos table
    const { error: dbError } = await supabase
      .from("profile_photos")
      .insert({ user_id: user.id, storage_path: fileName });

    if (dbError) {
      console.error("Error saving photo record:", dbError);
    }

    setUploading(false);
    return publicUrl;
  }, [user]);

  const uploadProfilePhoto = useCallback(async (file: File): Promise<string | null> => {
    const publicUrl = await uploadPhoto(file);
    
    if (publicUrl && user) {
      // Update profile's main photo_url
      await supabase
        .from("profiles")
        .update({ photo_url: publicUrl })
        .eq("id", user.id);
    }
    
    return publicUrl;
  }, [user, uploadPhoto]);

  const deletePhoto = useCallback(async (storagePath: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase.storage
      .from("profile-photos")
      .remove([storagePath]);

    if (!error) {
      await supabase
        .from("profile_photos")
        .delete()
        .eq("storage_path", storagePath)
        .eq("user_id", user.id);
    }

    return { error };
  }, [user]);

  return { uploadPhoto, uploadProfilePhoto, deletePhoto, uploading };
};

export const useVideoUpload = () => {
  const { user } = useAuthContext();
  const [uploading, setUploading] = useState(false);

  const uploadVideo = useCallback(async (file: File): Promise<string | null> => {
    if (!user) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return null;
    }

    if (!file.type.startsWith("video/")) {
      toast({ title: "Please select a video file", variant: "destructive" });
      return null;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Video must be less than 50MB", variant: "destructive" });
      return null;
    }

    setUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-videos")
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      setUploading(false);
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("profile-videos")
      .getPublicUrl(fileName);

    // Save to profile_videos table
    await supabase
      .from("profile_videos")
      .insert({ user_id: user.id, storage_path: fileName, is_intro_video: true });

    // Update profile
    await supabase
      .from("profiles")
      .update({ video_url: publicUrl, has_intro_video: true })
      .eq("id", user.id);

    setUploading(false);
    toast({ title: "Video uploaded successfully!" });
    return publicUrl;
  }, [user]);

  return { uploadVideo, uploading };
};
